import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import bidItemActions from "../../../../_actions/bidItemActions";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import LocationMultiselect from "../_common/LocationMultiselect";
import locationActions from "../../../../_actions/locationActions";
import _ from "lodash";
import AlertModal from "../../../_common/AlertModal";

export default function EditItemModalV2(props) {
  const { showModal, toggleModal, projectId, bidItemId } = props;

  const uomOptions = useRef([
    { value: "LS", label: "LS" },
    { value: "LF", label: "LF" },
    { value: "CY", label: "CY" },
    { value: "CF", label: "CF" },
    { value: "SQYD", label: "SQYD" },
    { value: "SQFT", label: "SQFT" },
    { value: "LB", label: "LB" },
    { value: "EA", label: "EA" },
    { value: "MFBM", label: "MFBM" },
    { value: "TON", label: "TON" },
    { value: "WDAY", label: "WDAY" },
    { value: "GAL", label: "GAL" },
    { value: "ACRE", label: "ACRE" },
    { value: "STA", label: "STA" },
    { value: "DAY", label: "DAY" },
    { value: "HR", label: "HR" },
    { value: "MO", label: "MO" },
    { value: "LNMI", label: "LNMI" },
  ]);

  const dispatch = useDispatch();

  const projLocationList = useSelector(
    (state) => state.locations.projLocationLists[projectId] || null
  );
  useEffect(() => !projLocationList && dispatch(locationActions.getAll(projectId)), []);

  const bidItemData = useSelector((state) =>
    bidItemId ? state.bidItems.openBidItemDetails[projectId][bidItemId] || null : null
  );
  const projDetails = useSelector((state) =>
    projectId ? state.projects.openProjectDetails[projectId] || null : null
  );
  const isNonCasProjectType = useMemo(
    () => projDetails && ["DO", "MINOR_B", "OTHER"].includes(projDetails.projectType),
    [projDetails]
  );

  const [_data, setData] = useState(bidItemData ? { ...bidItemData } : {});
  useEffect(() => setData(bidItemData ? { ...bidItemData } : {}), [bidItemData]);

  const [showAlert_hasLinks, setShowAlert_hasLinks] = useState(false);
  const toggleAlert_hasLinks = () => setShowAlert_hasLinks(!showAlert_hasLinks);

  const [itemNo, setItemNo] = useState((_data && _data.itemNo) || "");
  const [code, setCode] = useState((_data && _data.code) || "");
  const [description, setDescription] = useState((_data && _data.description) || "");
  const [selectedUnitOfMeasure, selectUnitOfMeasure] = useState(
    _data && _data.unitOfMeasure
      ? uomOptions.current.find((uo) => uo.value === _data.unitOfMeasure) || null
      : null
  );
  const [eeQuantity, setEeQuantity] = useState((_data && _data.eeQuantity) || "");
  const [bidUnitPrice, setBidUnitPrice] = useState((_data && _data.bidUnitPrice) || "");
  const [isFinalPay, setIsFinalPay] = useState(
    (_data && _data.isFinalPay) || false || false
  );

  // BidItemDto provides locations[], an array of @type {BidItemByLocationSummaryDtos}.
  // Purpose is to manage quantity re-allocations to genLoc when locations are deselected.
  // This is NOT used to manage location selections - locationIds[] handles that.
  const [locations, setLocations] = useState(
    _data && (_data.locations || []).map((ibl) => ({ ...ibl }))
  );

  // locationIds[] manages location selections. It is used onSubmit to rebuild
  // locations[] for the submit payload.
  const [locationIds, setLocationIds] = useState(
    (_data && (_data.locations || []).map((l) => l.locationId)) || []
  );

  // Whenever locations for an item are deselected, quantity breakdowns must
  // be adjusted the same way DoW handles deselection. This is provided as
  // deselectCallback into LocationMultiselect.
  const onLocationDeselect = useCallback((deselectedLocationId) => {
    // locations[] still contains the deselectedLocation at this point.
    let iblList = (locations || []).map((ibl) => ({ ...ibl }));

    // Since 'deselectedLocation' is NOT an ibl (it's from projLocationList),
    // find the matching ibl.
    const deselectedItemIndex = iblList.findIndex(
      (ibl) => ibl.locationId === deselectedLocationId
    );
    if (deselectedItemIndex < 0) return;

    const accumulator = (accum, currVal) => accum + currVal;
    const totalInputEeQtyForItem =
      iblList
        .map((ibl) => ibl.roadwayEeQuantity + ibl.structureEeQuantity)
        .reduce(accumulator) || 0;

    // Return Ibl's (strEeQty + rdwyEeQty) to genLoc's rdwyEeQty.
    // If there's an overrun, discard the overrun amount when returning.
    // (This is to force the return of overrun amount when deleting an ibl.)
    // If the (strEeQty + rdwyEeQty) of the ibl being deleted is less than
    // the overrun amount, nothing is returned.
    const genLoc = (projLocationList || []).find((l) => l.isGeneric);
    if (!genLoc) return;
    const genLocIbl = iblList.find((ibl) => ibl.locationId === genLoc.locationId);
    if (!genLocIbl) return;

    const strEeQtyToXfer = iblList[deselectedItemIndex].structureEeQuantity;
    const rdwyEeQtyToXfer = iblList[deselectedItemIndex].roadwayEeQuantity;

    // If there's no overrun, provide a simple x-fer to genLoc's rdwyEeQty
    if (totalInputEeQtyForItem <= bidItemData.eeQuantity) {
      genLocIbl.roadwayEeQuantity += _.round(rdwyEeQtyToXfer + strEeQtyToXfer, 3);
    } else {
      // Calculate the overrun amount.
      const overrunQty = totalInputEeQtyForItem - bidItemData.eeQuantity;
      genLocIbl.roadwayEeQuantity += _.round(
        Math.max(strEeQtyToXfer + rdwyEeQtyToXfer - overrunQty, 0),
        3
      );
    }

    setLocations(iblList.filter((ibl) => ibl.locationId !== deselectedLocationId));
  });

  const [formIsIncomplete, setFormIsIncomplete] = useState(false);
  const [formErrors, _setFormErrors] = useState({
    itemNo: null,
    code: null,
    description: null,
    unitOfMeasure: null,
    eeQuantity: null,
  });

  const validateForm = () => {
    const standardError = "Required";
    const _formErrors = {
      ...formErrors,
      itemNo: itemNo ? null : standardError,
      code: code || isNonCasProjectType ? null : standardError,
      description: description ? null : standardError,
      unitOfMeasure: selectedUnitOfMeasure ? null : standardError,
      eeQuantity: eeQuantity ? null : standardError,
    };
    _setFormErrors(_formErrors);
  };

  useEffect(
    () => setFormIsIncomplete(Object.values(formErrors).some((e) => e)),
    [formErrors]
  );

  useEffect(() => validateForm(), []);
  useEffect(() => validateForm(), [selectedUnitOfMeasure]);

  const handleBlur = (fieldName, value) => {
    validateForm();
    setData({ ..._data, [fieldName]: value });
  };

  const handleClick_cancel = (e) => {
    e.preventDefault();
    toggleModal();
  };

  const handleClick_save = async (e) => {
    e.preventDefault();

    if (formIsIncomplete) return;

    const _bidItemData = {
      ...(bidItemData || {}),
      projectId,
      itemNo: _data.itemNo,
      code: _data.code || "",
      description: _data.description,
      unitOfMeasure: _data.unitOfMeasure,
      eeQuantity: _data.eeQuantity,
      bidUnitPrice: _data.bidUnitPrice,
      isFinalPay: _data.isFinalPay || false,
      locations: locationIds.map((lid) => {
        const existBibl = (locations || []).find((l) => l.locationId === lid);
        return {
          bidItemId: bidItemId || 0,
          locationId: lid,
          roadwayEeQuantity: existBibl ? existBibl.roadwayEeQuantity : 0,
          structureEeQuantity: existBibl ? existBibl.structureEeQuantity : 0,
          roadwayCcoQuantity: existBibl ? existBibl.roadwayCcoQuantity : 0,
          structureCcoQuantity: existBibl ? existBibl.structureCcoQuantity : 0,
        };
      }),
    };

    let res;
    if (bidItemId) res = await dispatch(bidItemActions.update(_bidItemData));
    else res = await dispatch(bidItemActions.add(_bidItemData));
    if (res) toggleModal();
  };

  const closeBtn = (
    <button className="close" onClick={handleClick_cancel}>
      <FontAwesomeIcon icon="times" className="fa-fw text-white" />
    </button>
  );

  return (
    <React.Fragment>
      <Modal isOpen={showModal} size={"xl"} fade={false}>
        <ModalHeader className="bg-info text-white" close={closeBtn}>
          {bidItemData ? "Edit Item" : "Add Item"}
        </ModalHeader>
        <ModalBody>
          <div className="card m-2">
            <div className="card-body">
              <div className="form-row mb-2">
                <div className="form-group mb-0 col-2">
                  <label htmlFor="itemNo" className="mb-0">
                    <b>Item No:</b>*
                  </label>
                  <input
                    type="number"
                    name="itemNo"
                    id="itemNo"
                    className="form-control"
                    maxLength="3"
                    value={itemNo}
                    onChange={(e) => setItemNo(e.target.value)}
                    onBlur={(e) => handleBlur(e.target.name, e.target.value)}
                    disabled={!isNonCasProjectType}
                  />
                  {formErrors.itemNo && (
                    <span className="error-small">
                      <small>{formErrors.itemNo}</small>
                    </span>
                  )}
                </div>
                <div className="form-group mb-0 col-3">
                  <label htmlFor="code" className="mb-0">
                    {!isNonCasProjectType ? (
                      <>
                        <b>Code:</b>*
                      </>
                    ) : (
                      "Code:"
                    )}
                  </label>
                  <input
                    type="text"
                    name="code"
                    id="code"
                    className="form-control"
                    maxLength="15"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    onBlur={(e) => handleBlur(e.target.name, e.target.value)}
                    disabled={!isNonCasProjectType}
                  />
                  {formErrors.code && (
                    <span className="error-small">
                      <small>{formErrors.code}</small>
                    </span>
                  )}
                </div>
              </div>
              <div className="form-row mb-2">
                <div className="form-group mb-0 col-12">
                  <label htmlFor="description" className="mb-0">
                    <b>Description:</b>*
                  </label>
                  <input
                    type="text"
                    name="description"
                    id="description"
                    className="form-control"
                    maxLength="100"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    onBlur={(e) => handleBlur(e.target.name, e.target.value)}
                  />
                  {formErrors.description && (
                    <span className="error-small">
                      <small>{formErrors.description}</small>
                    </span>
                  )}
                </div>
              </div>
              <div className="form-row mb-2">
                <div className="form-group mb-0 col-4">
                  <label htmlFor="eeQuantity" className="mb-0">
                    <b>EE Qty:</b>*
                  </label>
                  <input
                    type="number"
                    name="eeQuantity"
                    id="eeQuantity"
                    className="form-control"
                    maxLength="30"
                    value={eeQuantity}
                    onChange={(e) => setEeQuantity(e.target.value)}
                    onBlur={(e) => handleBlur(e.target.name, e.target.value)}
                  />
                  {formErrors.eeQuantity && (
                    <span className="error-small">
                      <small>{formErrors.eeQuantity}</small>
                    </span>
                  )}
                </div>
                <div className="form-group mb-0 col-4">
                  <label htmlFor="unitOfMeasure" className="mb-0">
                    <b>UoM:</b>*
                  </label>
                  <Select
                    name="unitOfMeasure"
                    id="unitOfMeasure"
                    value={selectedUnitOfMeasure}
                    options={uomOptions.current}
                    onChange={(selected) => {
                      selectUnitOfMeasure(selected);
                      handleBlur("unitOfMeasure", selected ? selected.value : null);
                    }}
                    isClearable
                    styles={{
                      // Fixes the overlapping problem of the component
                      menu: (provided) => ({ ...provided, zIndex: 9999 }),
                    }}
                  />
                  {formErrors.unitOfMeasure && (
                    <span className="error-small">
                      <small>{formErrors.unitOfMeasure}</small>
                    </span>
                  )}
                </div>
                <div className="form-group mb-0 col-4">
                  <label htmlFor="bidUnitPrice" className="mb-0">
                    <b>Unit Price:</b>*
                  </label>
                  <div className="input-group">
                    <div className="input-group-prepend">
                      <span className="input-group-text">$</span>
                    </div>
                    <input
                      type="number"
                      name="bidUnitPrice"
                      id="bidUnitPrice"
                      className="form-control"
                      maxLength="20"
                      value={bidUnitPrice}
                      onChange={(e) => setBidUnitPrice(e.target.value)}
                      onBlur={(e) => handleBlur(e.target.name, e.target.value)}
                    />
                  </div>
                  {formErrors.bidUnitPrice && (
                    <span className="error-small">
                      <small>{formErrors.bidUnitPrice}</small>
                    </span>
                  )}
                </div>
              </div>
              <hr className="my-2" />
              <div className="form-row form-group mb-2">
                <div className="col-sm-3 my-auto">
                  <div className="custom-control-inline custom-control custom-checkbox">
                    <input
                      type="checkbox"
                      name="isFinalPay"
                      id="isFinalPay"
                      className="custom-control-input my-auto"
                      checked={isFinalPay}
                      onChange={(e) => {
                        setIsFinalPay(e.target.checked);
                        handleBlur(e.target.name, e.target.checked);
                      }}
                    />
                    <label htmlFor="isFinalPay" className="custom-control-label my-auto">
                      Final Pay?
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <LocationMultiselect
            projectId={projectId}
            wpType="Bid Item"
            locationIds={locationIds}
            setLocationIds={setLocationIds}
            getDeselectAllowed={async (locationId) => {
              const hasLinks = await dispatch(
                bidItemActions.locationHasLinks(bidItemId, locationId)
              );
              return !hasLinks;
            }}
            toggleDeselectNotAllowedAlert={toggleAlert_hasLinks}
            deselectCallback={onLocationDeselect}
          />
        </ModalBody>
        <ModalFooter className="bg-lightgray">
          <div className="form-row form-group">
            <button
              className="btn btn-secondary mr-2"
              color="secondary"
              onClick={toggleModal}
            >
              Cancel
            </button>
            <button
              id="saveItem"
              name="saveItem"
              className="btn btn-primary"
              onClick={handleClick_save}
            >
              <b>{bidItemData ? "Save" : "Add"}</b>
            </button>
          </div>
        </ModalFooter>
      </Modal>
      {showAlert_hasLinks && (
        <AlertModal
          alertTitle="Warning"
          alertTitleClass="bg-warning"
          alertTitleIcon="exclamation-triangle"
          alertText="This location cannot be removed from this item since it has references to one or more documents in this project."
          acceptCaption="OK"
          acceptClass="secondary"
          provideReject={false}
          handleAlertAction={toggleAlert_hasLinks}
          showAlertModal={showAlert_hasLinks}
          toggleAlertModal={toggleAlert_hasLinks}
        />
      )}
    </React.Fragment>
  );
}
